import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Video, Image, Film, Star, Lock, Play, Eye, Calendar,
  User, LogOut, Crown, Sparkles, Download, Clock, Search, Filter, X
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import GrainOverlay from "../components/GrainOverlay";
import Footer from "../components/MemberAreaFooter";
import MessageCreatorWidget from "../components/MessageCreatorWidget";
import AdCarousel from "../components/AdCarousel";
import GalleryMediaViewer from "../components/GalleryMediaViewer";
import GalleryImage from "../components/GalleryImage";
import BookMeExperience from "../components/BookMeExperience";

// Upgrade CTAs and the VIP Store tab; hidden while paid tiers are off.
const SHOW_UPGRADE_CTAS = false;
const SHOW_VIP_STORE_TAB = false;

export default function MembersArea() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [activeTab, setActiveTab] = useState("videos"); // "videos" | "photos" | "bts" | "vip-store" | "cast" | "newsfeed" | "book-me"
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [btsContent, setBtsContent] = useState([]);
  const [storeVideos, setStoreVideos] = useState([]); // VIP-exclusive store videos
  const [castMembers, setCastMembers] = useState([]); // Porn actors and crew
  const [posts, setPosts] = useState([]); // Newsfeed posts
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCast, setSelectedCast] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [activePhotoFolderId, setActivePhotoFolderId] = useState(null);
  const visiblePhotoFolders = photos.filter(folder => (folder.parent_id || null) === activePhotoFolderId);
  const photoBreadcrumbs = [];
  const photoFolderById = new Map(photos.map(folder => [folder.id, folder]));
  let photoFolder = photoFolderById.get(activePhotoFolderId);
  const seenPhotoFolderIds = new Set();
  while (photoFolder && !seenPhotoFolderIds.has(photoFolder.id)) {
    seenPhotoFolderIds.add(photoFolder.id);
    photoBreadcrumbs.unshift(photoFolder);
    photoFolder = photoFolderById.get(photoFolder.parent_id);
  }
  const activePhotoFolder = photoFolderById.get(activePhotoFolderId);

  // Auto-refresh listener for global data changes
  useEffect(() => {
    const handleDataChange = (event) => {
      console.log("[MembersArea] Global data change detected:", event.detail);
      loadMemberContent();
      checkSubscription();
    };

    window.addEventListener("gio:data-changed", handleDataChange);
    return () => window.removeEventListener("gio:data-changed", handleDataChange);
  }, []);

  // Check subscription on mount
  useEffect(() => {
    // Check for admin preview mode
    const adminPreviewTier = searchParams.get("admin_preview");
    if (adminPreviewTier) {
      // Admin preview mode - bypass auth
      setSubscription({
        tier: adminPreviewTier,
        status: "active",
        admin_preview: true,
      });
      loadMemberContent(adminPreviewTier);
      setLoading(false);
      toast.success(`Admin Preview: ${adminPreviewTier.toUpperCase()} Area`, { duration: 3000 });
      return;
    }

    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      // Coming from Stripe checkout
      verifySession(sessionId);
    } else {
      // Check if user already has active subscription
      checkSubscription();
    }
  }, []);

  const verifySession = async (sessionId) => {
    try {
      // Poll for payment confirmation
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        const res = await api.get(`/api/subscriptions/payment-status/${sessionId}`);

        if (res.data.payment_status === "paid") {
          toast.success("Welcome to the Members Area! 🎉");
          await loadMemberContent();
          setLoading(false);
          return;
        }

        if (res.data.status === "expired" || res.data.status === "failed") {
          toast.error("Payment failed. Please try again.");
          navigate("/subscribe");
          return;
        }

        // Wait 2s before next poll
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }

      toast.error("Payment verification timed out. Please contact support.");
      navigate("/subscribe");
    } catch (err) {
      console.error("Session verification failed:", err);
      toast.error("Failed to verify payment");
      navigate("/subscribe");
    }
  };

  const checkSubscription = async () => {
    try {
      // Check for JWT token from member_auth.py
      const memberToken = localStorage.getItem("memberToken");

      if (!memberToken) {
        toast.error("Please log in to access the Members Area");
        navigate("/members/login");
        return;
      }

      // Verify token and get subscription status
      const res = await api.get("/api/subscriptions/me", {
        headers: { Authorization: `Bearer ${memberToken}` }
      });

      setSubscription(res.data);
      await loadMemberContent();
    } catch (err) {
      console.error("Subscription check failed:", err);
      toast.error("Please log in to continue");
      navigate("/members/login");
    } finally {
      setLoading(false);
    }
  };

  const loadMemberContent = async (previewTier = null) => {
    try {
      // Use preview tier or get from subscription
      const userTier = previewTier || subscription?.tier;

      // Load videos from catalog (only ones with member/vip visibility set)
      // Gallery is loaded first so its member-safe feed survives catalog auth errors.
      if (previewTier) {
        setPhotos([]);
      } else {
        try {
          const galleryRes = await api.get("/api/member-content/gallery/folders");
          setPhotos(galleryRes.data.folders || []);
        } catch (galleryError) {
          console.error("Failed to load member photo gallery:", galleryError);
          setPhotos([]);
        }
      }

      const videosRes = await api.get("/api/catalog");
      const memberVideos = videosRes.data.filter(v => {
        const vis = v.visibility || [];
        // Only show videos that have visibility explicitly set
        if (vis.length === 0) return false;

        if (userTier === "vip") {
          // VIP can see both member and vip videos
          return vis.includes("members") || vis.includes("vip");
        } else {
          // Regular members can see member videos + VIP videos (with restrictions)
          return vis.includes("members") || vis.includes("vip");
        }
      }).map(v => {
        // Mark videos as VIP-exclusive if they're only in VIP tier
        const vis = v.visibility || [];
        return {
          ...v,
          isVipExclusive: vis.includes("vip") && !vis.includes("members")
        };
      });
      setVideos(memberVideos);

      // Load Store videos for VIP members only
      if (userTier === "vip") {
        try {
          const storeRes = await api.get("/api/store/videos");
          setStoreVideos(storeRes.data || []);
        } catch (err) {
          console.error("Failed to load store videos:", err);
        }
      }

      // Load Cast Members (Porn Actors & Crew)
      // Skip in admin preview mode (no auth token)
      if (!previewTier) {
        try {
          const castRes = await api.get("/api/crm/talent", { _silent401: true });
          setCastMembers(castRes.data || []);
        } catch (err) {
          console.error("Failed to load cast members:", err);
          // Silently fail - cast members are optional for members area
        }
      }

      // Load Newsfeed Posts
      try {
        const memberToken = localStorage.getItem("memberToken");
        const headers = memberToken ? { Authorization: `Bearer ${memberToken}` } : {};
        const postsRes = await api.get("/api/newsfeed/posts", { headers });
        setPosts(postsRes.data.posts || []);
      } catch (err) {
        console.error("Failed to load posts:", err);
      }

      // Load BTS content from catalog (videos with "bts" visibility)
      const btsVideos = videosRes.data.filter(v => {
        const vis = v.visibility || [];
        if (!vis.includes("bts")) return false;
        
        // VIP can see all BTS content
        if (userTier === "vip") return true;
        
        // Members can see BTS marked for members or see VIP-only with restrictions
        return true;
      }).map(v => {
        const vis = v.visibility || [];
        return {
          ...v,
          isVipExclusive: vis.includes("vip") && !vis.includes("members")
        };
      });
      setBtsContent(btsVideos);
    } catch (err) {
      console.error("Failed to load content:", err);
      toast.error("Failed to load content");
    }
  };

  const handleWatchVideo = (video) => {
    // Navigate to DRM video player
    navigate(`/members-area/watch/${video.id}`);
  };

  const handleLogout = () => {
    localStorage.removeItem("memberToken");
    localStorage.removeItem("memberUser");
    toast.success("Logged out successfully");
    navigate("/");
  };

  // Get all unique tags from videos
  const allTags = useMemo(() => {
    const tagSet = new Set();
    videos.forEach(v => {
      if (v.tags && Array.isArray(v.tags)) {
        v.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [videos]);

  // Get all unique cast from videos
  const allCast = useMemo(() => {
    const castSet = new Set();
    videos.forEach(v => {
      if (v.cast) {
        // Split by comma and trim
        v.cast.split(',').forEach(name => {
          const trimmed = name.trim();
          if (trimmed) castSet.add(trimmed);
        });
      }
    });
    return Array.from(castSet).sort();
  }, [videos]);

  // Filter videos based on search, tags, and cast
  const filteredVideos = useMemo(() => {
    return videos.filter(v => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchTitle = v.title?.toLowerCase().includes(query);
        const matchCast = v.cast?.toLowerCase().includes(query);
        const matchTags = v.tags?.some(tag => tag.toLowerCase().includes(query));
        const matchCity = v.city?.toLowerCase().includes(query);
        if (!matchTitle && !matchCast && !matchTags && !matchCity) return false;
      }

      // Tags filter
      if (selectedTags.length > 0) {
        const videoTags = v.tags || [];
        const hasAllTags = selectedTags.every(tag => videoTags.includes(tag));
        if (!hasAllTags) return false;
      }

      // Cast filter
      if (selectedCast.length > 0) {
        const videoCast = v.cast || "";
        const hasAnyCast = selectedCast.some(cast => videoCast.includes(cast));
        if (!hasAnyCast) return false;
      }

      return true;
    });
  }, [videos, searchQuery, selectedTags, selectedCast]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleCast = (cast) => {
    setSelectedCast(prev => 
      prev.includes(cast) ? prev.filter(c => c !== cast) : [...prev, cast]
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedTags([]);
    setSelectedCast([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
        style={{ background: "linear-gradient(to bottom, #000000, #0a0a0a)" }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading your exclusive content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#000000" }}>
      <GrainOverlay />
      <MessageCreatorWidget />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(0,0,0,0.8)" }}>
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="text-purple-400" size={24} />
              <h1 className="text-xl font-bold text-white">Members Area</h1>
              {subscription && (
                <span className="hidden sm:inline-flex px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs font-semibold text-purple-300">
                  {subscription.tier === "vip" ? "VIP" : "Member"}
                </span>
              )}
            </div>
            
            <button onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-white transition">
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Welcome Banner */}
      <div className="relative overflow-hidden border-b border-white/5"
        style={{ 
          background: "linear-gradient(135deg, #1a0a2e 0%, #0f0519 100%)",
        }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 py-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-4">
              <Sparkles className="text-purple-400" size={16} />
              <span className="text-sm text-purple-300 font-medium">Exclusive Content</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Welcome Back! 👋
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg">
              Enjoy unlimited access to all member content, photo galleries, and behind-the-scenes exclusives
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-white/5 sticky top-[73px] z-30 backdrop-blur-xl"
        style={{ background: "rgba(0,0,0,0.8)" }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex gap-6 overflow-x-auto scrollbar-hide">
            <button onClick={() => setActiveTab("videos")}
              className={`flex items-center gap-2 px-4 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                activeTab === "videos"
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <Video size={18} />
              <span>Videos</span>
              {videos.length > 0 && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                  {videos.length}
                </span>
              )}
            </button>
            
            <button onClick={() => setActiveTab("photos")}
              className={`flex items-center gap-2 px-4 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                activeTab === "photos"
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <Image size={18} />
              <span>Photo Galleries</span>
            </button>

            <button onClick={() => setActiveTab("book-me")}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-4 font-medium transition ${
                activeTab === "book-me" ? "border-purple-500 text-purple-400" : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <Calendar size={18} />
              <span>Book Me</span>
            </button>
            
            <button onClick={() => setActiveTab("bts")}
              className={`flex items-center gap-2 px-4 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                activeTab === "bts"
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <Film size={18} />
              <span>BTS</span>
            </button>
            
            {/* VIP Store Tab - Only for VIP members */}
            {SHOW_VIP_STORE_TAB && subscription?.tier === "vip" && (
              <button onClick={() => setActiveTab("vip-store")}
                className={`flex items-center gap-2 px-4 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                  activeTab === "vip-store"
                    ? "border-purple-500 text-purple-400"
                    : "border-transparent text-zinc-400 hover:text-white"
                }`}>
                <Crown size={18} />
                <span>VIP Store</span>
                {storeVideos.length > 0 && (
                  <span className="px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full">
                    {storeVideos.length}
                  </span>
                )}
              </button>
            )}
            
            {/* Cast & Crew Tab */}
            <button onClick={() => setActiveTab("cast")}
              className={`flex items-center gap-2 px-4 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                activeTab === "cast"
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <Star size={18} />
              <span>Cast & Crew</span>
              {castMembers.length > 0 && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                  {castMembers.length}
                </span>
              )}
            </button>
            
            {/* Newsfeed Tab */}
            <button onClick={() => setActiveTab("newsfeed")}
              className={`flex items-center gap-2 px-4 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                activeTab === "newsfeed"
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <span>Newsfeed</span>
              {posts.length > 0 && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                  {posts.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Area with Ad Carousels */}
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex gap-6">
          {/* Left Ad Sidebar */}
          <div className="hidden xl:block flex-shrink-0">
            <AdCarousel 
              placement="members" 
              position="left" 
              className="w-64 h-[500px] sticky top-24"
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
        {/* Videos Tab */}
        {activeTab === "videos" && (
          <div>
            {/* Filters Section */}
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 mb-6">
              {/* Search Bar */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title, cast, tags, location..."
                    className="w-full bg-zinc-800/50 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                      <X size={16} />
                    </button>
                  )}
                </div>
                <button onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition ${
                    showFilters || selectedTags.length > 0 || selectedCast.length > 0
                      ? "bg-purple-600 text-white"
                      : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                  }`}>
                  <Filter size={18} />
                  <span className="hidden sm:inline">Filters</span>
                  {(selectedTags.length + selectedCast.length > 0) && (
                    <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                      {selectedTags.length + selectedCast.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Advanced Filters (collapsible) */}
              {showFilters && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                  {/* Tags Filter */}
                  {allTags.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-zinc-300 mb-2">Filter by Tags</label>
                      <div className="flex flex-wrap gap-2">
                        {allTags.map(tag => (
                          <button key={tag}
                            onClick={() => toggleTag(tag)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                              selectedTags.includes(tag)
                                ? "bg-purple-600 text-white"
                                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                            }`}>
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cast Filter */}
                  {allCast.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-zinc-300 mb-2">Filter by Cast</label>
                      <div className="flex flex-wrap gap-2">
                        {allCast.slice(0, 15).map(cast => (
                          <button key={cast}
                            onClick={() => toggleCast(cast)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                              selectedCast.includes(cast)
                                ? "bg-purple-600 text-white"
                                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                            }`}>
                            {cast}
                          </button>
                        ))}
                        {allCast.length > 15 && (
                          <span className="text-xs text-zinc-600 px-3 py-1.5">+{allCast.length - 15} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Clear Filters */}
                  {(selectedTags.length > 0 || selectedCast.length > 0 || searchQuery) && (
                    <button onClick={clearFilters}
                      className="text-sm text-purple-400 hover:text-purple-300 underline transition">
                      Clear all filters
                    </button>
                  )}
                </div>
              )}

              {/* Active Filters Summary */}
              {(selectedTags.length > 0 || selectedCast.length > 0) && !showFilters && (
                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                  {selectedTags.map(tag => (
                    <span key={tag}
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-purple-600 text-white rounded-full text-xs font-medium">
                      #{tag}
                      <button onClick={() => toggleTag(tag)} className="hover:bg-white/20 rounded-full p-0.5">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  {selectedCast.map(cast => (
                    <span key={cast}
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-purple-600 text-white rounded-full text-xs font-medium">
                      {cast}
                      <button onClick={() => toggleCast(cast)} className="hover:bg-white/20 rounded-full p-0.5">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-white">Member Videos</h3>
                <p className="text-sm text-zinc-500 mt-1">
                  {filteredVideos.length === videos.length ? (
                    <span>{videos.length} video{videos.length !== 1 ? 's' : ''} available</span>
                  ) : (
                    <span>Showing {filteredVideos.length} of {videos.length} video{videos.length !== 1 ? 's' : ''}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-sm text-zinc-500">{videos.length} videos available</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      viewMode === "grid"
                        ? "bg-purple-600 text-white"
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}>
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      viewMode === "list"
                        ? "bg-purple-600 text-white"
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}>
                    List
                  </button>
                </div>
              </div>
            </div>
            
            {filteredVideos.length === 0 ? (
              <div className="text-center py-16">
                <Video className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-500">
                  {videos.length === 0 ? "No videos available yet" : "No videos match your filters"}
                </p>
                {videos.length > 0 && (
                  <button onClick={clearFilters}
                    className="mt-4 text-purple-400 hover:text-purple-300 underline text-sm">
                    Clear filters
                  </button>
                )}
              </div>
            ) : viewMode === "list" ? (
              <div className="space-y-4">
                {filteredVideos.map((video) => (
                  <div key={video.id}
                    className="group bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300 cursor-pointer"
                    onClick={() => !video.isVipExclusive || subscription?.tier === "vip" ? handleWatchVideo(video) : null}>
                    <div className="flex gap-4 p-4">
                      {/* Large Thumbnail */}
                      <div className="w-96 h-56 flex-shrink-0 bg-zinc-800 rounded-lg overflow-hidden relative">
                        {video.thumbnail_url || video.thumbnail ? (
                          <img src={video.thumbnail_url || video.thumbnail}
                            alt={video.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : video.mux_playback_id ? (
                          <img src={`https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg?width=640&time=5`}
                            alt={video.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film size={48} className="text-zinc-700" />
                          </div>
                        )}
                        
                        {/* VIP EXCLUSIVE Badge */}
                        {video.isVipExclusive && (
                          <div className="absolute top-2 left-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                            <Crown size={12} />
                            VIP EXCLUSIVE
                          </div>
                        )}
                        
                        {/* Play Overlay or VIP Lock */}
                        {video.isVipExclusive && subscription?.tier !== "vip" ? (
                          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                            <div className="text-center px-6">
                              <Lock size={48} className="text-yellow-500 mx-auto mb-3" />
                              <p className="text-white font-bold text-lg mb-2">VIP ONLY</p>
                              {SHOW_UPGRADE_CTAS && (
                                <>
                                  <p className="text-zinc-400 text-sm mb-4">Upgrade to VIP to watch full video</p>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate("/subscribe"); }}
                                    className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold rounded-lg transition">
                                    Upgrade to VIP
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-purple-600 flex items-center justify-center">
                              <Play size={28} fill="white" className="text-white ml-1" />
                            </div>
                          </div>
                        )}
                        
                        {/* Duration Badge */}
                        {(video.runtime_minutes || video.duration) && (
                          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-medium rounded">
                            {video.runtime_minutes ? `${video.runtime_minutes} min` : video.duration}
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 flex flex-col min-w-0">
                        <h4 className="text-2xl font-bold text-white mb-2">{video.title}</h4>
                        
                        {video.description && (
                          <p className="text-zinc-400 text-sm mb-3 line-clamp-2">
                            {video.description}
                          </p>
                        )}
                        
                        {/* Cast */}
                        {video.cast && (
                          <div className="flex items-center gap-2 mb-3">
                            <User size={14} className="text-purple-400" />
                            <span className="text-sm text-zinc-300">{video.cast}</span>
                          </div>
                        )}
                        
                        {/* Metadata */}
                        <div className="flex flex-wrap gap-4 text-xs text-zinc-500 mb-3">
                          {video.shoot_date && (
                            <div className="flex items-center gap-1">
                              <Calendar size={12} />
                              <span>{new Date(video.shoot_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {video.city && (
                            <div className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{video.city}</span>
                            </div>
                          )}
                          {video.views !== undefined && (
                            <div className="flex items-center gap-1">
                              <Eye size={12} />
                              <span>{video.views} views</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Tags */}
                        {video.tags && video.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {video.tags.slice(0, 6).map((tag) => (
                              <span key={tag}
                                className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs font-medium">
                                #{tag}
                              </span>
                            ))}
                            {video.tags.length > 6 && (
                              <span className="text-xs text-zinc-600">+{video.tags.length - 6} more</span>
                            )}
                          </div>
                        )}
                        
                        {/* Action Button */}
                        <button className="mt-auto w-fit px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all shadow-lg shadow-purple-500/30 flex items-center gap-2">
                          <Play size={16} fill="white" />
                          Watch Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVideos.map((video) => (
                  <div key={video.id}
                    className="group relative bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300">
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-zinc-800 overflow-hidden">
                      {video.thumbnail_url || video.thumbnail ? (
                        <img src={video.thumbnail_url || video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : video.mux_playback_id ? (
                        <img src={`https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg?width=640&time=5`}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film size={48} className="text-zinc-700" />
                        </div>
                      )}
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <button onClick={() => handleWatchVideo(video)}
                          className="flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-full transition">
                          <Play size={20} />
                          <span>Watch Now</span>
                        </button>
                      </div>
                      
                      {/* Duration Badge */}
                      {(video.runtime_minutes || video.duration) && (
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-medium rounded">
                          {video.runtime_minutes ? `${video.runtime_minutes} min` : video.duration}
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <h4 className="font-semibold text-white mb-2 line-clamp-2">{video.title}</h4>
                      {video.description && (
                        <p className="text-sm text-zinc-400 line-clamp-2 mb-2">{video.description}</p>
                      )}
                      
                      {video.cast && (
                        <div className="flex items-center gap-1 mb-2">
                          <User size={12} className="text-purple-400" />
                          <span className="text-xs text-zinc-500 line-clamp-1">{video.cast}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-zinc-500">
                        {video.shoot_date && (
                          <div className="flex items-center gap-1">
                            <Calendar size={12} />
                            <span>{new Date(video.shoot_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {video.views !== undefined && (
                          <div className="flex items-center gap-1">
                            <Eye size={12} />
                            <span>{video.views}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "book-me" && <BookMeExperience />}

        {/* Photo Galleries Tab */}
        {activeTab === "photos" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Photo Galleries</h3>
              <p className="text-sm text-zinc-500">{photos.length} folders</p>
            </div>

            <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm" aria-label="Gallery breadcrumbs" data-testid="member-gallery-breadcrumbs">
              <button onClick={() => setActivePhotoFolderId(null)} className="text-zinc-200 hover:text-white">All folders</button>
              {photoBreadcrumbs.map(folder => (
                <React.Fragment key={folder.id}>
                  <span className="text-zinc-600">/</span>
                  <button onClick={() => setActivePhotoFolderId(folder.id)} className="text-zinc-300 hover:text-white">{folder.title}</button>
                </React.Fragment>
              ))}
            </nav>

            {activePhotoFolder && (
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-4">
                <div>
                  <h4 className="font-semibold text-white">{activePhotoFolder.title}</h4>
                  {activePhotoFolder.description && <p className="mt-1 text-sm text-zinc-400">{activePhotoFolder.description}</p>}
                </div>
                <button type="button" onClick={() => setActivePhotoFolderId(activePhotoFolder.parent_id || null)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActivePhotoFolderId(activePhotoFolder.parent_id || null);
                    }
                  }}
                  aria-expanded="true" aria-label={`Close ${activePhotoFolder.title}`}
                  data-testid={`member-gallery-folder-${activePhotoFolder.id}`}
                  className="rounded-md bg-white/5 px-3 py-2 text-sm text-zinc-200 hover:bg-white/10">
                  Close folder
                </button>
                <button onClick={() => setSelectedGallery(activePhotoFolder)} disabled={!activePhotoFolder.picture_count}
                  className="rounded-md bg-cyan-500/15 px-3 py-2 text-sm text-cyan-100 hover:bg-cyan-500/25 disabled:opacity-40"
                  data-testid="member-gallery-view-current">
                  View {activePhotoFolder.picture_count} photos
                </button>
              </div>
            )}
            
            {photos.length === 0 ? (
              <div className="text-center py-16">
                <Image className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-500">No photo galleries available yet</p>
              </div>
            ) : visiblePhotoFolders.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 py-12 text-center text-sm text-zinc-500">
                This folder has no subfolders. View its photos above.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="member-gallery-folder-grid">
                {visiblePhotoFolders.map((gallery) => (
                  <div key={gallery.id}
                    className="group relative bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300">
                    <button type="button" className="relative block aspect-[4/3] w-full bg-zinc-800 overflow-hidden cursor-pointer"
                      aria-expanded="false" aria-label={`Open ${gallery.title}`}
                      data-testid={`member-gallery-folder-${gallery.id}`}
                      onKeyDown={event => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setActivePhotoFolderId(gallery.id);
                        }
                      }}
                      onClick={() => setActivePhotoFolderId(gallery.id)}>
                      {gallery.cover_image_url ? (
                        <GalleryImage src={gallery.cover_image_url} alt={gallery.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image size={48} className="text-zinc-700" />
                        </div>
                      )}
                      
                      {/* VIP EXCLUSIVE Badge */}
                      {gallery.isVipExclusive && (
                        <div className="absolute top-2 left-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                          <Crown size={12} />
                          VIP EXCLUSIVE
                        </div>
                      )}
                      
                      {/* Photo Count Badge */}
                      <div className="absolute top-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-medium rounded flex items-center gap-1">
                        <Image size={12} />
                        {gallery.picture_count}
                      </div>
                      
                      {/* VIP Lock Overlay */}
                      {gallery.isVipExclusive && subscription?.tier !== "vip" && (
                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                          <div className="text-center px-4">
                            <Lock size={32} className="text-yellow-500 mx-auto mb-2" />
                            <p className="text-white font-bold text-xs">VIP ONLY</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Hover Overlay */}
                      {(!gallery.isVipExclusive || subscription?.tier === "vip") && (
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="flex items-center gap-2 px-4 py-2 bg-purple-600 rounded-full text-white text-sm font-semibold">
                            <Eye size={16} />
                            View Gallery
                          </div>
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h4 className="font-semibold text-white mb-1 line-clamp-2">{gallery.title}</h4>
                      </div>
                    </button>
                    
                    {/* Gallery Details */}
                    <div className="p-4 space-y-2">
                      {/* Cast */}
                      {gallery.creator_name && (
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                          <User size={14} />
                          <span className="line-clamp-1">{gallery.creator_name}</span>
                        </div>
                      )}
                      
                      {/* Location */}
                      {gallery.city && (
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{gallery.city}</span>
                        </div>
                      )}
                      
                      {/* Tags */}
                      {gallery.tags && gallery.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {gallery.tags.slice(0, 3).map((tag) => (
                            <span key={tag}
                              className="px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded text-xs">
                              #{tag}
                            </span>
                          ))}
                          {gallery.tags.length > 3 && (
                            <span className="text-xs text-zinc-600">+{gallery.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                      <span className="text-[10px] uppercase text-zinc-500">{gallery.access_level} access</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BTS Tab */}
        {activeTab === "bts" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Behind The Scenes</h3>
              <p className="text-sm text-zinc-500">{btsContent.length} items</p>
            </div>
            
            {btsContent.length === 0 ? (
              <div className="text-center py-16">
                <Film className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-500">No BTS content available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {btsContent.map((item) => (
                  <div key={item.id}
                    className="group relative bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300 cursor-pointer"
                    onClick={() => !item.isVipExclusive || subscription?.tier === "vip" ? handleWatchVideo(item) : null}>
                    <div className="relative aspect-video bg-zinc-800 overflow-hidden">
                      {item.thumbnail_url || item.thumbnail ? (
                        <img src={item.thumbnail_url || item.thumbnail}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : item.mux_playback_id ? (
                        <img src={`https://image.mux.com/${item.mux_playback_id}/thumbnail.jpg?width=640&time=5`}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film size={48} className="text-zinc-700" />
                        </div>
                      )}
                      
                      {/* VIP EXCLUSIVE Badge */}
                      {item.isVipExclusive && (
                        <div className="absolute top-2 left-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                          <Crown size={12} />
                          VIP EXCLUSIVE
                        </div>
                      )}
                      
                      {/* Play Overlay or VIP Lock */}
                      {item.isVipExclusive && subscription?.tier !== "vip" ? (
                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                          <div className="text-center px-6">
                            <Lock size={40} className="text-yellow-500 mx-auto mb-2" />
                            <p className="text-white font-bold text-sm mb-2">VIP ONLY</p>
                            {SHOW_UPGRADE_CTAS && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate("/subscribe"); }}
                              className="px-4 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black text-xs font-bold rounded-lg transition">
                              Upgrade to VIP
                            </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <button
                            onClick={() => handleWatchVideo(item)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-full transition">
                            <Play size={16} />
                            Watch
                          </button>
                        </div>
                      )}
                      
                      {(item.runtime_minutes || item.duration) && (
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-medium rounded">
                          {item.runtime_minutes ? `${item.runtime_minutes} min` : item.duration}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <h4 className="font-semibold text-white mb-1 line-clamp-2">{item.title}</h4>
                      {item.cast && (
                        <div className="flex items-center gap-1 text-xs text-zinc-400 mb-1">
                          <User size={12} />
                          <span>{item.cast}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-xs text-zinc-500">
                        <Clock size={12} />
                        <span>{item.upload_date ? new Date(item.upload_date).toLocaleDateString() : 'Recently added'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VIP Store Tab - Exclusive for VIP Members */}
        {SHOW_VIP_STORE_TAB && activeTab === "vip-store" && subscription?.tier === "vip" && (
          <div>
            <div className="mb-8 p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <Crown className="text-purple-400 mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">VIP Exclusive Store Access</h3>
                  <p className="text-zinc-300 text-sm">
                    As a VIP member, you have <span className="text-purple-400 font-semibold">FREE unlimited access</span> to all premium store videos. 
                    No additional payment required!
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Store Videos</h3>
              <p className="text-sm text-zinc-500">{storeVideos.length} videos available</p>
            </div>
            
            {storeVideos.length === 0 ? (
              <div className="text-center py-16">
                <Crown className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-500 mb-2">No store videos available yet</p>
                <p className="text-xs text-zinc-600">Check back soon for exclusive premium content!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {storeVideos.map((video) => (
                  <div key={video.video_id}
                    onClick={() => handleWatchVideo({ id: video.video_id })}
                    className="group relative bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300 cursor-pointer">
                    <div className="flex flex-col sm:flex-row gap-4 p-4">
                      {/* Thumbnail */}
                      <div className="relative w-full sm:w-64 aspect-video bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={
                            video.thumbnail_url || 
                            video.thumbnail ||
                            video.cover_image ||
                            (video.mux_playback_id ? `https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg?width=640&height=360&fit_mode=smartcrop&time=3` : null) ||
                            "https://via.placeholder.com/640x360/1f1f1f/666666?text=No+Thumbnail"
                          }
                          alt={video.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/640x360/1f1f1f/666666?text=No+Thumbnail";
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        />
                        
                        {/* VIP Free Badge */}
                        <div className="absolute top-2 right-2 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                          <Crown size={10} />
                          FREE
                        </div>
                        
                        {video.duration && (
                          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-medium rounded">
                            {video.duration}
                          </div>
                        )}
                        
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-purple-600 text-white p-3 rounded-full">
                            <Play size={20} fill="currentColor" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <h4 className="font-semibold text-white text-lg line-clamp-1">{video.title}</h4>
                          <div className="flex items-center gap-2 text-xs text-zinc-500 flex-shrink-0">
                            <span className="line-through">${video.price || video.rental_price}</span>
                            <span className="text-purple-400 font-semibold whitespace-nowrap">FREE for VIP</span>
                          </div>
                        </div>
                        
                        {video.description && (
                          <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{video.description}</p>
                        )}
                        
                        {/* Cast */}
                        {video.cast && video.cast.length > 0 && (
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <User size={14} className="text-zinc-500" />
                            <div className="flex gap-2 flex-wrap">
                              {video.cast.slice(0, 3).map((actor, idx) => (
                                <span key={idx} className="text-xs text-zinc-400 px-2 py-1 bg-zinc-800/50 rounded">
                                  {actor.name || actor.stage_name}
                                </span>
                              ))}
                              {video.cast.length > 3 && (
                                <span className="text-xs text-zinc-500">+{video.cast.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Meta Info */}
                        <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
                          {video.release_date && (
                            <div className="flex items-center gap-1">
                              <Calendar size={12} />
                              <span>{new Date(video.release_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {video.views && (
                            <div className="flex items-center gap-1">
                              <Eye size={12} />
                              <span>{video.views} views</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cast & Crew Tab */}
        {activeTab === "cast" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Cast & Crew</h3>
              <p className="text-sm text-zinc-500">{castMembers.length} talent</p>
            </div>
            
            {castMembers.length === 0 ? (
              <div className="text-center py-16">
                <Star className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-500">No cast members available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {castMembers.map((member) => (
                  <div key={member.id}
                    className="group relative bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300 cursor-pointer">
                    <div className="relative aspect-[3/4] bg-zinc-800 overflow-hidden">
                      <img src={member.profile_pic_url || member.photos?.[0] || "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400"}
                        alt={member.stage_name || member.legal_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      
                      {/* Type Badge */}
                      {member.type && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-medium rounded backdrop-blur-sm">
                          {member.type}
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    
                    <div className="p-3">
                      <h4 className="font-semibold text-white text-sm line-clamp-1 mb-1">
                        {member.stage_name || member.legal_name}
                      </h4>
                      
                      {member.role && (
                        <p className="text-xs text-zinc-400 line-clamp-1 mb-2">{member.role}</p>
                      )}
                      
                      {/* Stats */}
                      {member.stats && (
                        <div className="flex flex-wrap gap-1 text-xs text-zinc-500">
                          {member.stats.height && (
                            <span className="px-1.5 py-0.5 bg-zinc-800/50 rounded">{member.stats.height}</span>
                          )}
                          {member.body_type && (
                            <span className="px-1.5 py-0.5 bg-zinc-800/50 rounded">{member.body_type}</span>
                          )}
                        </div>
                      )}
                      
                      {/* Social Links */}
                      {(member.x_url || member.instagram_url) && (
                        <div className="flex gap-2 mt-2">
                          {member.x_url && (
                            <a href={member.x_url} target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-zinc-400 hover:text-purple-400 transition">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                          {member.instagram_url && (
                            <a href={member.instagram_url} target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-zinc-400 hover:text-pink-400 transition">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Newsfeed Tab */}
        {activeTab === "newsfeed" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Latest Updates</h3>
              <p className="text-sm text-zinc-500">{posts.length} posts</p>
            </div>
            
            {posts.length === 0 ? (
              <div className="text-center py-16">
                <svg className="mx-auto text-zinc-600 mb-4 w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                <p className="text-zinc-500">No posts yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {posts.map((post) => (
                  <div key={post.post_id}
                    className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300">
                    {/* Post Media */}
                    {post.media_url && (
                      <div className="relative aspect-video bg-zinc-800 overflow-hidden">
                        {post.media_type === "video" ? (
                          <video 
                            src={post.media_url}
                            controls
                            className="w-full h-full object-cover"
                            poster={post.media_url.replace(/\.[^/.]+$/, "") + ".jpg"} />
                        ) : (
                          <img src={post.media_url}
                            alt={post.title}
                            className="w-full h-full object-cover" />
                        )}
                      </div>
                    )}
                    
                    {/* Post Content */}
                    <div className="p-6">
                      {/* Visibility Badge */}
                      <div className="flex items-center gap-2 mb-3">
                        {post.visibility === "vip" && (
                          <span className="px-2 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Crown size={12} />
                            VIP Only
                          </span>
                        )}
                        {post.visibility === "members" && (
                          <span className="px-2 py-1 bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-semibold rounded-full">
                            Members Only
                          </span>
                        )}
                        <span className="text-xs text-zinc-500">
                          {new Date(post.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                      
                      <h4 className="text-xl font-bold text-white mb-3">{post.title}</h4>
                      <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
                        {post.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
          </div>

          {/* Right Ad Sidebar */}
          <div className="hidden xl:block flex-shrink-0">
            <AdCarousel 
              placement="members" 
              position="right" 
              className="w-64 h-[500px] sticky top-24"
            />
          </div>
        </div>

        {/* Bottom Ad Carousel */}
        <div className="mt-12">
          <AdCarousel 
            placement="members" 
            position="bottom" 
            className="w-full h-32"
          />
        </div>
      </div>

      <Footer legacy />

      {/* Photo Gallery Modal */}
      {selectedGallery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setSelectedGallery(null)}>
          <div className="relative w-full max-w-6xl max-h-[90vh] bg-zinc-900 border border-white/10 rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-zinc-900/95 border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">{selectedGallery.title}</h2>
                <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400">
                  {selectedGallery.creator_name && (
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>{selectedGallery.creator_name}</span>
                    </div>
                  )}
                  {selectedGallery.city && (
                    <div className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span>{selectedGallery.city}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Image size={14} />
                    <span>{selectedGallery.picture_count} photos</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedGallery(null)}
                className="text-zinc-400 hover:text-white transition">
                <X size={24} />
              </button>
            </div>

            {/* Tags */}
            {selectedGallery.tags && selectedGallery.tags.length > 0 && (
              <div className="px-6 py-3 border-b border-white/10 flex flex-wrap gap-2">
                {selectedGallery.tags.map((tag) => (
                  <span key={tag}
                    className="px-3 py-1 bg-purple-500/10 text-purple-400 rounded-full text-xs font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Photo Grid */}
            <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedGallery.pictures.map((picture, idx) => (
                  <div key={picture.id || idx}
                    className="group relative aspect-square bg-zinc-800 rounded-lg overflow-hidden cursor-pointer"
                    onClick={() => setSelectedPicture(picture)}
                    data-testid={`member-gallery-picture-card-${picture.id}`}>
                    <GalleryImage src={picture.poster_url || picture.thumbnail_url || picture.image_url}
                      alt={picture.title || `${selectedGallery.title} - Photo ${idx + 1}`}
                      imageTestId={`member-gallery-picture-${picture.id}`}
                      fallbackTestId="member-gallery-image-fallback"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    {picture.media_type === "video" && <>
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center"><Play size={28} className="fill-black/60 text-white drop-shadow" /></span>
                      {Number.isFinite(Number(picture.duration)) && <span className="absolute bottom-2 right-2 rounded bg-black/80 px-1.5 py-0.5 text-[10px] text-white">{Math.floor(Number(picture.duration) / 60)}:{String(Math.floor(Number(picture.duration) % 60)).padStart(2, "0")}</span>}
                    </>}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Eye size={24} className="text-white" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gallery details */}
            <div className="bg-zinc-900/95 border-t border-white/10 px-6 py-4 flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {selectedGallery.description || selectedGallery.title}
              </p>
              <button onClick={() => setSelectedGallery(null)} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">Close</button>
            </div>
          </div>
        </div>
      )}
      {selectedPicture && <GalleryMediaViewer
        picture={selectedPicture}
        playbackUrl={selectedPicture.media_type === "video" ? `/api/member-content/gallery/pictures/${selectedPicture.id}/playback-token` : null}
        onClose={() => setSelectedPicture(null)}
      />}
    </div>
  );
}
